/*
 *  TTTech nerve-management-system
 *  Copyright(c) 2021. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */
import semver from 'semver';
import useUtilsStore from '@/store/modules/pinia/utils/_api_request_handler';

export function getKeyByValue(object, value) {
  return Object.keys(object).find((key) => object[key] === value);
}

export function isObject(entity) {
  return typeof entity === 'object' && entity !== null;
}

export function dateFormatByLanguage(value) {
  // TODO Change format of date by selected language
  return new Date(value).getDate()
    ? new Date(value).toLocaleDateString(['en-EN'], {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : value;
}

export function timeFormatHourMinSeconds(duration) {
  // Hours, minutes and seconds
  const hrs = Math.floor(duration / 3600);
  const mins = Math.floor((duration % 3600) / 60);
  const secs = Math.floor(duration) % 60;

  // Output like "1:01" or "4:03:59" or "123:03:59"
  let ret = '';

  if (hrs > 0) {
    ret += `${hrs}h ${mins < 10 ? '0' : ''}`;
  }

  ret += `${mins}min ${secs < 10 ? '0' : ''}`;
  ret += `${secs}s`;
  return ret;
}

export function versionWithoutPrereleaseTags(version) {
  if (!version || !semver.valid(version)) {
    return null;
  }
  const prereleaseTags = semver.prerelease(version);
  if (!prereleaseTags) {
    return version;
  }
  const major = semver.major(version);
  const minor = semver.minor(version);
  const patch = semver.patch(version);

  return `${major}.${minor}.${patch}`;
}

export function isFullVersionGreaterThan(version, referenceVersion) {
  const nodeVersion = versionWithoutPrereleaseTags(version);
  if (!nodeVersion || !referenceVersion || !semver.valid(referenceVersion)) {
    return false;
  }
  return semver.gt(nodeVersion, referenceVersion);
}
export function isNoPrereleasedVersionGreaterThanOrEqualTo(version, referenceVersion) {
  const nodeVersion = versionWithoutPrereleaseTags(version);
  if (!nodeVersion || !referenceVersion || !semver.valid(referenceVersion)) {
    return false;
  }
  return semver.gte(nodeVersion, referenceVersion);
}

/**
 * @description If tag is not defined, add latest
 * @param {string} imageName The name of the docker image
 */
export function addTagLatestIfMissing(imageName) {
  const splitByColon = imageName.split(':');
  return splitByColon.length === 1 ? `${imageName}:latest` : imageName;
}

export function compareTwoDockerImageNamesAreEqual(name1, name2, internalDockerRegistry) {
  if (!name1 || !name2) {
    return false;
  }
  const serviceImageName = internalDockerRegistry ? name2.split('/').reverse()[0] : name2;
  const sourceName = internalDockerRegistry ? name1.split('/').reverse()[0] : name1;

  return Boolean(addTagLatestIfMissing(serviceImageName) === addTagLatestIfMissing(sourceName));
}

async function getFetchErrorMessageKey(response) {
  const defaultKey = 'errorMessages.defaultError';

  try {
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      return defaultKey;
    }

    const payload = await response.clone().json();
    const errorObject = Array.isArray(payload) ? payload[0] : payload;
    const errorCode = errorObject?.errorCode;

    return errorCode ? `errorMessages.${errorCode}` : defaultKey;
  } catch {
    return defaultKey;
  }
}

export async function checkUrlAndRedirect({ url, method = 'GET', credentials = 'include', cb, location = false }) {
  const utilsStore = useUtilsStore();

  const response = await fetch(url, { method, credentials });
  if ((location && response.ok) || (!location && response.ok && !response.redirected)) {
    cb();
  } else if (response.status === 403 || (response.ok && response.redirected)) {
    window.dispatchEvent(new CustomEvent('logout-required'));
    utilsStore.showCustomToast({
      text: 'errorMessages.session_expired',
      color: 'red',
      showClose: true,
      permanent: true,
    });
  } else {
    const errorMessageKey = await getFetchErrorMessageKey(response);
    utilsStore.showCustomToast({
      text: errorMessageKey,
      color: 'red',
      showClose: true,
    });
  }
}

export function castArrayOfStrings(value, defaultValue) {
  if (!Array.isArray(value)) {
    return [value !== undefined ? value : defaultValue];
  }
  if (value[0] !== undefined) {
    return value;
  }
  return [defaultValue];
}
export function castArray(value) {
  return Array.isArray(value) ? value : [value];
}
export function castBoolean(value) {
  return value === 'true';
}
export function transformStringToBoolean(value, defaultValue) {
  const result = Array.isArray(value) ? value[0] : value;
  // The boolean value is retrieved from table component,
  // while the string is extracted from the query parameters in the URL.
  const map = {
    true: true,
    false: false,
  };

  // Check if result exists in map, otherwise return default
  return Object.prototype.hasOwnProperty.call(map, result) ? map[result] : defaultValue;
}

/**
 * Checks whether a capability exists in the provided list.
 * @param {Array<{name: string}>} capabilities - List of capability objects.
 * @param {string} capabilityName - Capability name to check.
 * @returns {boolean} True if capability exists, otherwise false.
 */
export function hasCapability(capabilities, capabilityName) {
  return capabilities.some((capability) => capability.name === capabilityName);
}

export function getWorkloadStatusColor(status) {
  const colorMap = {
    started: 'success',
    stopped: 'gray',
    suspended: 'warning',
    error: 'error',
    removing_failed: 'error',
    idle: 'tertiary',
    removing: 'primary',
    restarting: 'primary',
    creating: 'primary',
    stopping: 'primary',
    suspending: 'primary',
    starting: 'primary',
    resuming: 'primary',
    partially_running: 'green',
  };

  return colorMap[status] || undefined;
}

/**
 * Textual comparison (alphabetical). Uses localeCompare with sensitivity to
 * base characters so case differences don't change order unless necessary.
 * Null/empty values are treated as smaller (sorted first in ascending order, last in descending order).
 * @param {*} a - First value to compare
 * @param {*} b - Second value to compare
 * @returns {number} -1 if a < b, 1 if a > b, 0 if equal
 */
export function compareText(a, b) {
  const as = a == null ? '' : String(a);
  const bs = b == null ? '' : String(b);

  if (as === '' && bs === '') {
    return 0;
  }
  if (as === '') {
    return -1;
  }
  if (bs === '') {
    return 1;
  }

  return as.localeCompare(bs, undefined, { sensitivity: 'base', numeric: false });
}

/**
 * Parses a date string in 'DD/MM/YYYY, HH:mm:ss' format to a millisecond timestamp.
 * @param {string} date - Date string to parse
 * @returns {number} Milliseconds since epoch, or NaN if invalid format
 */
const parseDateStringToTimestamp = (date) => {
  if (!date || typeof date !== 'string') {
    return NaN;
  }
  const s = String(date).trim();

  // Parse format 'DD/MM/YYYY, HH:mm:ss'
  const match = s.match(/^(\d{2})\/(\d{2})\/(\d{4}),\s+(\d{2}):(\d{2}):(\d{2})$/);
  if (match) {
    const [, day, month, year, hours, minutes, seconds] = match;
    const d = new Date(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10),
      parseInt(hours, 10),
      parseInt(minutes, 10),
      parseInt(seconds, 10),
    );
    return d.getTime();
  }

  return NaN;
};

/**
 * Date comparison by actual time. Expects date strings in 'DD/MM/YYYY, HH:mm:ss' format.
 * Invalid dates are treated as smaller (pushed to the beginning in ascending order, to the end in descending order).
 * @param {string} a - First date string
 * @param {string} b - Second date string
 * @returns {number} -1 if a < b, 1 if a > b, 0 if equal
 */
export function compareDates(a, b) {
  const ta = parseDateStringToTimestamp(a);
  const tb = parseDateStringToTimestamp(b);
  const aNaN = Number.isNaN(ta);
  const bNaN = Number.isNaN(tb);
  if (aNaN && bNaN) {
    return 0;
  }
  if (aNaN) {
    return -1;
  }
  if (bNaN) {
    return 1;
  }
  if (ta < tb) {
    return -1;
  }
  if (ta > tb) {
    return 1;
  }
  return 0;
}

/**
 * Parses an IPv4 address string into an array of 4 numeric octets.
 * Returns null if the string is not a valid IPv4 address.
 * @param {string} ip - IPv4 address string (e.g., '192.168.1.1')
 * @returns {number[]|null} Array of 4 octets, or null if invalid
 */
function parseIPv4ToSegments(ip) {
  if (!ip || typeof ip !== 'string') {
    return null;
  }
  const parts = ip.trim().split('.');
  if (parts.length !== 4) {
    return null;
  }
  for (const p of parts) {
    if (p === '') {
      return null;
    }
    if (!/^[0-9]+$/.test(p)) {
      return null;
    }
    const n = Number(p);
    if (n < 0 || n > 255) {
      return null;
    }
  }
  return parts.map(Number);
}

/**
 * Parses an IPv6 address string into an array of 8 numeric 16-bit groups.
 * Handles :: (zero compression) notation. Returns null if invalid.
 * @param {string} ip - IPv6 address string (e.g., '2001:db8::1')
 * @returns {number[]|null} Array of 8 groups (0-65535), or null if invalid
 */
function parseIPv6ToSegments(ip) {
  if (typeof ip !== 'string') {
    return null;
  }

  const working = String(ip).trim();
  if (!working.includes(':')) {
    return null;
  }

  const parts = working.split('::');
  if (parts.length > 2) {
    return null;
  }

  let groups;
  if (parts.length === 1) {
    groups = working.split(':');
  } else {
    const left = parts[0] === '' ? [] : parts[0].split(':');
    const right = parts[1] === '' ? [] : parts[1].split(':');
    const missing = 8 - (left.length + right.length);

    if (missing < 0 || missing > 8) {
      return null;
    }

    groups = [...left, ...Array(missing).fill('0'), ...right];
  }

  if (groups.length !== 8) {
    return null;
  }
  for (const g of groups) {
    if (g === '') {
      return null;
    }
    if (g.length > 4) {
      return null;
    }
    if (!/^[0-9a-fA-F]*$/.test(g)) {
      return null;
    }
  }

  return groups.map((g) => parseInt(g, 16) || 0);
}

/**
 * Compares two arrays of IP address segments numerically (left-to-right).
 * @param {number[]} segments1 - First segment array
 * @param {number[]} segments2 - Second segment array
 * @returns {number} -1 if segments1 < segments2, 1 if greater, 0 if equal
 */
function compareIPAddressSegments(segments1, segments2) {
  const len = segments1.length;
  for (let i = 0; i < len; i++) {
    if (segments1[i] < segments2[i]) {
      return -1;
    }
    if (segments1[i] > segments2[i]) {
      return 1;
    }
  }
  return 0;
}

/**
 * Compare IP addresses. IPv4 addresses sort before IPv6 addresses.
 * Within each family, comparison is by numeric value per segment.
 * IPv4: 20.12.12.12 < 20.13.12.12 < 190.12.12.12
 * IPv6: 2001:db8::1 < 2001:db8::2
 * Invalid/empty IPs are treated as smaller (sorted to the beginning in ascending order, to the end in descending order).
 * @param {*} a - First IP address (string or null)
 * @param {*} b - Second IP address (string or null)
 * @returns {number} -1 if a < b, 1 if a > b, 0 if equal
 */
export function compareIP(a, b) {
  const as = a == null ? '' : String(a).trim();
  const bs = b == null ? '' : String(b).trim();
  const aSegmentsV4 = parseIPv4ToSegments(as);
  const bSegmentsV4 = parseIPv4ToSegments(bs);
  const aSegmentsV6 = parseIPv6ToSegments(as);
  const bSegmentsV6 = parseIPv6ToSegments(bs);
  const aIsV4 = aSegmentsV4 != null;
  const bIsV4 = bSegmentsV4 != null;
  const aIsV6 = aSegmentsV6 != null;
  const bIsV6 = bSegmentsV6 != null;
  const aIsValid = aIsV4 || aIsV6;
  const bIsValid = bIsV4 || bIsV6;

  // Both invalid: fall back to text comparison
  if (!aIsValid && !bIsValid) {
    return compareText(as, bs);
  }

  // One valid, one invalid: invalid is "smaller" (sorted to beginning)
  if (aIsValid && !bIsValid) {
    return 1;
  }
  if (!aIsValid && bIsValid) {
    return -1;
  }

  // Both valid: IPv4 < IPv6
  if (aIsV4 && bIsV6) {
    return -1;
  }
  if (aIsV6 && bIsV4) {
    return 1;
  }

  // Both same type: compare segments
  if (aIsV4 && bIsV4) {
    return compareIPAddressSegments(aSegmentsV4, bSegmentsV4);
  }
  // Both IPv6
  return compareIPAddressSegments(aSegmentsV6, bSegmentsV6);
}

/**
 * Version comparison using semver. Coerces version strings to valid semver.
 * Compares numerically by segment (3.20.4 < 10.0.0).
 * Invalid/uncoercible versions are treated as smaller (pushed to the beginning in ascending order, to the end in descending order).
 * @param {*} a - First version string
 * @param {*} b - Second version string
 * @returns {number} -1 if a < b, 1 if a > b, 0 if equal
 */
export function compareVersions(a, b) {
  const av = semver.coerce(String(a || ''));
  const bv = semver.coerce(String(b || ''));
  const aNull = av == null;
  const bNull = bv == null;
  if (aNull && bNull) {
    return 0;
  }
  if (aNull) {
    return -1;
  }
  if (bNull) {
    return 1;
  }
  return semver.compare(av.version, bv.version);
}
